import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from '@/utils/icons';

interface TablePaginationProps {
  page: number;
  totalPages: number;
  total: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  pageSizeOptions?: number[];
}

export function TablePagination({
  page,
  totalPages,
  total,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [25, 50, 100],
}: TablePaginationProps) {
  if (total === 0) return null;

  return (
    <div className="dls-flex dls-items-center dls-justify-between dls-px-6 dls-py-3 dls-border-t">
      <div className="dls-flex dls-items-center dls-gap-2 dls-text-sm dls-text-muted-foreground">
        <span>
          Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total}
        </span>
        {onPageSizeChange && (
          <>
            <Select
              value={String(pageSize)}
              onValueChange={(v) => {
                onPageSizeChange(Number(v));
                onPageChange(1);
              }}
            >
              <SelectTrigger className="dls-w-[72px] dls-h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {pageSizeOptions.map((size) => (
                  <SelectItem key={size} value={String(size)}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span>per page</span>
          </>
        )}
      </div>
      {totalPages > 1 && (
        <div className="dls-flex dls-items-center dls-gap-1">
          <Button
            variant="secondary"
            size="icon"
            className="dls-h-8 dls-w-8"
            onClick={() => onPageChange(1)}
            disabled={page === 1}
            aria-label="First page"
          >
            <ChevronsLeft className="dls-h-4 dls-w-4" />
          </Button>
          <Button
            variant="secondary"
            size="icon"
            className="dls-h-8 dls-w-8"
            onClick={() => onPageChange(page - 1)}
            disabled={page === 1}
            aria-label="Previous page"
          >
            <ChevronLeft className="dls-h-4 dls-w-4" />
          </Button>
          <span className="dls-text-sm dls-px-2">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="secondary"
            size="icon"
            className="dls-h-8 dls-w-8"
            onClick={() => onPageChange(page + 1)}
            disabled={page === totalPages}
            aria-label="Next page"
          >
            <ChevronRight className="dls-h-4 dls-w-4" />
          </Button>
          <Button
            variant="secondary"
            size="icon"
            className="dls-h-8 dls-w-8"
            onClick={() => onPageChange(totalPages)}
            disabled={page === totalPages}
            aria-label="Last page"
          >
            <ChevronsRight className="dls-h-4 dls-w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
